import Loader from 'components/Loader'
import Switch, { OptionType } from 'components/Switch'
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import styled from 'styled-components'
import { ThemeContext } from 'styled-components'
import { ThemedText } from 'theme'
import { formatAmount, formatDollarAmount } from 'utils/formatDollarAmt'
import { formatPercentage } from 'utils/numbers'

import { useIslandContext } from '../hooks/useIslandContext'
import { GraphFlex } from './styleds'

const MainContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  background: ${({ theme }) => theme.cardSecondary};
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
`

const ContentWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const HeaderContainer = styled.div`
  padding: 12px 16px;
  z-index: 1;
  max-width: 85%;
`

const ChartContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    flex-direction: column;
  `}
`

const CompactPieContainer = styled.div`
  width: 50%;
  min-height: 180px;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    width: 100%;
    min-height: 160px;
  `}
`

const ChartLegendContainer = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 200px;
  height: 100%;
  padding: 10px 16px 10px 40px;
  overflow-y: auto;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    width: 100%;
    max-height: 140px;
    padding: 0 16px 8px 16px;
    margin-top: 8px;
    border-top: 1px solid ${theme.cardBorderPrimary};
  `}
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: ${({ theme }) => theme.textSecondary};
  white-space: nowrap;
  margin-bottom: 2px;
  justify-content: end;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    font-size: 11px;
    padding: 4px 0;
  `}
`

const LegendContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  overflow: hidden;
  gap: 8px;
`

const AddressLink = styled.a`
  color: ${({ theme }) => theme.textSecondary};
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;

  &:hover {
    color: ${({ theme }) => theme.textPrimary};
    text-decoration: underline;
  }

  ${({ theme }) => theme.mediaWidth.upToSmall`
    max-width: 240px;
  `}
`

const ColorBox = styled.div<{ color: string }>`
  min-width: 8px;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background-color: ${({ color }) => color};
  margin-right: 6px;
`

const OthersLabel = styled.span`
  flex: 1;
  min-width: 0;
`

const ValueColumn = styled.span`
  min-width: 44px;
  text-align: right;
  flex-shrink: 0;
`

const ToggleContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0;
  margin-top: auto;
`

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  padding: 16px 8px;

  ${({ theme }) => theme.mediaWidth.upToSmall`
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  `}

  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    grid-template-columns: 1fr;
  `}
`

const StatBox = styled.div`
  background: ${({ theme }) => theme.cardSecondary};
  border-radius: 8px;
  padding: 12px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};

  ${({ theme }) => theme.mediaWidth.upToSmall`
    padding: 10px 12px;
  `}
`

interface UserPosition {
  balanceUSD: number
  farmBalanceUSD: number
  vaultBalanceUSD: number
  externalBalanceUSD: number
  baultBalanceUSD: number
  balance: number
  farmBalance: number
  vaultBalance: number
  externalBalance: number
  baultBalance: number
  user: string
}

interface IslandHoldersCardProps {
  userPositions?: UserPosition[]
  totalTVL?: number
  isLoading?: boolean
}

const COLORS = [
  '#8884d8',
  '#83a6ed',
  '#8dd1e1',
  '#82ca9d',
  '#a4de6c',
  '#d0ed57',
  '#ffc658',
  '#ff8042',
  '#ff6361',
  '#bc5090',
  '#808080',
]

const useResponsiveRadius = () => {
  const [radius, setRadius] = useState(100)

  const updateRadius = useCallback(() => {
    const width = window.innerWidth
    if (width > 1200) {
      setRadius(100)
    } else if (width > 800) {
      setRadius(90)
    } else {
      setRadius(75)
    }
  }, [])

  useEffect(() => {
    updateRadius()
    window.addEventListener('resize', updateRadius)
    return () => window.removeEventListener('resize', updateRadius)
  }, [updateRadius])

  return radius
}

const formatLPTokenAmount = (amount: number) => {
  if (!amount || amount === 0) return '0'

  if (amount >= 0.001) {
    return formatAmount(amount)
  }

  const str = amount.toString()

  if (str.indexOf('e') === -1) {
    return formatAmount(amount)
  }

  const parts = str.split('e-')
  if (parts.length === 2) {
    const base = parseFloat(parts[0])
    const exponent = parseInt(parts[1])

    const baseFormatted = base.toFixed(4).replace(/\.?0+$/, '')
    const leadingZeros = exponent - 1

    return `0.${'0'.repeat(leadingZeros)}${baseFormatted.replace('.', '')}`
  }

  return formatAmount(amount)
}

const HoldersDistributionView: React.FC<IslandHoldersCardProps> = () => {
  const context = useIslandContext()
  const pieRadius = useResponsiveRadius()
  const islandsData = context.poolApiResponse
  const isLoading = false

  const systemAddresses = useMemo(() => {
    const addresses = new Set<string>()

    if (islandsData?.farm?.id) {
      addresses.add(islandsData.farm.id.toLowerCase())
    }

    if (islandsData?.baults) {
      islandsData.baults.forEach((bault) => {
        if (bault.id) {
          addresses.add(bault.id.toLowerCase())
        }
      })
    }

    return addresses
  }, [islandsData?.farm?.id, islandsData?.baults])

  const userPositions = useMemo(() => {
    const allPositions = islandsData?.userPositions || []
    return allPositions.filter((pos) => !systemAddresses.has(pos.user.toLowerCase()))
  }, [islandsData?.userPositions, systemAddresses])

  const theme = useContext(ThemeContext)
  const [chartData, setChartData] = useState<any[]>([])
  const [balanceType, setBalanceType] = useState<'poolBalance' | 'farmBalance' | 'baultBalance'>('farmBalance')

  const totalLpSupply = useMemo(() => islandsData?.tokenLp?.totalSupply || 0, [islandsData?.tokenLp])
  const lpTokenPriceUSD = useMemo(() => islandsData?.tokenLp?.price || 0, [islandsData?.tokenLp])

  const hasBaults = useMemo(() => {
    return islandsData?.baults && islandsData.baults.length > 0
  }, [islandsData?.baults])

  const totalFarmLp = useMemo(
    () =>
      userPositions.reduce((sum, pos) => {
        const balance = (pos.farmBalance || 0) + (pos.externalBalance || 0) + (pos.baultBalance || 0)
        return sum + Math.max(0, balance)
      }, 0),
    [userPositions]
  )

  const totalBaultLp = useMemo(
    () => userPositions.reduce((sum, pos) => sum + Math.max(0, pos.baultBalance || 0), 0),
    [userPositions]
  )

  const totalPoolLp = useMemo(
    () => userPositions.reduce((sum, pos) => sum + Math.max(0, pos.balance || 0), 0),
    [userPositions]
  )

  const balanceOptions = useMemo(() => {
    const baseOptions: OptionType<'poolBalance' | 'farmBalance' | 'baultBalance'>[] = [
      { value: 'poolBalance' as const, label: 'Pool Distribution' },
      { value: 'farmBalance' as const, label: 'Staked Balance' },
    ]

    if (hasBaults) {
      baseOptions.push({ value: 'baultBalance', label: 'Bault Balance' })
    }

    return baseOptions
  }, [hasBaults])

  const getPositionBalance = useCallback((pos: UserPosition, type: typeof balanceType) => {
    switch (type) {
      case 'poolBalance':
        return pos.balance || 0
      case 'farmBalance':
        return (pos.farmBalance || 0) + (pos.externalBalance || 0) + (pos.baultBalance || 0)
      case 'baultBalance':
        return pos.baultBalance || 0
      default:
        return 0
    }
  }, [])

  const getPositionUSDValue = useCallback((pos: UserPosition, type: typeof balanceType) => {
    switch (type) {
      case 'poolBalance':
        return pos.balanceUSD || 0
      case 'farmBalance':
        return (pos.farmBalanceUSD || 0) + (pos.externalBalanceUSD || 0) + (pos.baultBalanceUSD || 0)
      case 'baultBalance':
        return pos.baultBalanceUSD || 0
      default:
        return 0
    }
  }, [])

  useEffect(() => {
    if (!userPositions?.length || !totalLpSupply) return

    let denominator: number
    switch (balanceType) {
      case 'farmBalance':
        denominator = totalFarmLp > 0 ? totalFarmLp : totalLpSupply
        break
      case 'baultBalance':
        denominator = totalBaultLp > 0 ? totalBaultLp : totalLpSupply
        break
      case 'poolBalance':
        denominator = totalPoolLp > 0 ? totalPoolLp : totalLpSupply
        break
      default:
        denominator = totalLpSupply
        break
    }

    const nonZeroPositions = userPositions.filter((pos) => {
      return getPositionBalance(pos, balanceType) > 0
    })

    const sortedPositions = [...nonZeroPositions].sort((a, b) => {
      return getPositionBalance(b, balanceType) - getPositionBalance(a, balanceType)
    })

    const topHolders = sortedPositions.slice(0, 10)

    const othersTotal = sortedPositions.slice(10).reduce((sum, pos) => {
      return sum + getPositionBalance(pos, balanceType)
    }, 0)

    const data = [
      ...topHolders.map((holder) => {
        const lpValue = getPositionBalance(holder, balanceType)
        const percentValue = (lpValue / denominator) * 100
        const usdValue = getPositionUSDValue(holder, balanceType)

        return {
          name: `${holder.user.slice(0, 6)}...${holder.user.slice(-4)}`,
          value: lpValue,
          fullAddress: holder.user,
          balanceUSD: usdValue,
          percent: percentValue,
          lpAmount: lpValue,
        }
      }),
    ]

    if (othersTotal > 0) {
      const percentValue = (othersTotal / denominator) * 100
      const othersBalanceUSD = othersTotal * lpTokenPriceUSD

      data.push({
        name: 'Others',
        value: othersTotal,
        fullAddress: 'Others',
        balanceUSD: othersBalanceUSD,
        percent: percentValue,
        lpAmount: othersTotal,
      })
    }

    setChartData(data)
  }, [
    userPositions,
    balanceType,
    totalLpSupply,
    totalFarmLp,
    totalBaultLp,
    lpTokenPriceUSD,
    totalPoolLp,
    getPositionBalance,
    getPositionUSDValue,
  ])

  const getBalanceTypeDescription = useCallback((type: typeof balanceType) => {
    switch (type) {
      case 'poolBalance':
        return 'total pool balance'
      case 'farmBalance':
        return 'staked LP tokens'
      case 'baultBalance':
        return 'bault LP tokens'
      default:
        return 'LP tokens'
    }
  }, [])

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload
      return (
        <div
          style={{
            background: theme.cardPrimary,
            border: `1px solid ${theme.backgroundOutline}`,
            padding: '0.75rem',
            borderRadius: '8px',
            zIndex: 100,
          }}
        >
          <p style={{ color: theme.textPrimary, fontWeight: 'bold', marginBottom: '0.25rem' }}>{data.name}</p>
          {data.fullAddress && data.fullAddress !== 'Others' && (
            <p style={{ color: theme.textSecondary, fontSize: '12px', marginBottom: '0.25rem' }}>{data.fullAddress}</p>
          )}
          <p style={{ color: theme.textPrimary, fontSize: '14px' }}>{formatLPTokenAmount(data.lpAmount)} LP tokens</p>{' '}
          <p style={{ color: theme.textSecondary, fontSize: '12px' }}>
            {formatPercentage(data.percent)} of {getBalanceTypeDescription(balanceType)}
          </p>
          {data.balanceUSD > 0 && (
            <p style={{ color: theme.textSecondary, fontSize: '12px' }}>
              {formatDollarAmount(data.balanceUSD)} USD value
            </p>
          )}
        </div>
      )
    }
    return null
  }

  const CustomLegend = () => (
    <ChartLegendContainer>
      {chartData.map((entry, index) => (
        <LegendItem key={`legend-${index}`}>
          <ColorBox color={COLORS[index % COLORS.length]} />
          <LegendContent>
            {entry.fullAddress && entry.fullAddress !== 'Others' ? (
              <AddressLink
                href={`https://debank.com/profile/${entry.fullAddress}`}
                target="_blank"
                rel="noopener noreferrer"
                title={entry.fullAddress}
              >
                {entry.fullAddress}
              </AddressLink>
            ) : (
              <OthersLabel>{entry.name}</OthersLabel>
            )}
            <ValueColumn>{formatDollarAmount(entry.balanceUSD)}</ValueColumn>
            <ValueColumn>{formatPercentage(entry.percent)}</ValueColumn>
          </LegendContent>
        </LegendItem>
      ))}
    </ChartLegendContainer>
  )

  const top10Percent = chartData.reduce((sum, holder, index) => {
    if (index < 10) return sum + parseFloat(holder.percent)
    return sum
  }, 0)

  const totalHolders =
    userPositions?.filter((pos) => {
      return getPositionBalance(pos, balanceType) > 0
    }).length || 0

  const largestPositionLP = userPositions?.length
    ? Math.max(...userPositions.map((pos) => getPositionBalance(pos, balanceType)))
    : 0

  const largestPositionUser = userPositions?.find((pos) => getPositionBalance(pos, balanceType) === largestPositionLP)
  const largestPositionUSD = largestPositionUser ? getPositionUSDValue(largestPositionUser, balanceType) : 0

  const largeHoldersCount =
    userPositions?.filter((pos) => {
      const value = getPositionUSDValue(pos, balanceType)
      return value > 100000
    }).length || 0

  const mediumHoldersCount =
    userPositions?.filter((pos) => {
      const value = getPositionUSDValue(pos, balanceType)
      return value > 10000 && value <= 100000
    }).length || 0

  if (isLoading) {
    return (
      <GraphFlex>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>
          <Loader size="40px" />
        </div>
      </GraphFlex>
    )
  }

  return (
    <Fragment>
      <MainContainer>
        <ContentWrapper>
          <HeaderContainer>
            <ThemedText.DeprecatedSubHeader>Pool Holders Distribution</ThemedText.DeprecatedSubHeader>
            <ThemedText.DeprecatedMain fontSize={12} color={theme.textSecondary}>
              Top 10 holders control {top10Percent.toFixed(2)}% of {getBalanceTypeDescription(balanceType)}
            </ThemedText.DeprecatedMain>
          </HeaderContainer>

          {chartData.length > 0 ? (
            <Fragment>
              <ChartContainer>
                <CompactPieContainer>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={pieRadius}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </CompactPieContainer>
                <CustomLegend />
              </ChartContainer>
              <StatsGrid>
                <StatBox>
                  <ThemedText.DeprecatedBody color={theme.textSecondary} fontSize="12px">
                    Total Holders
                  </ThemedText.DeprecatedBody>
                  <ThemedText.DeprecatedBody color={theme.textPrimary}>{totalHolders}</ThemedText.DeprecatedBody>
                </StatBox>
                <StatBox>
                  <ThemedText.DeprecatedBody color={theme.textSecondary} fontSize="12px">
                    Largest Position
                  </ThemedText.DeprecatedBody>
                  <ThemedText.DeprecatedBody color={theme.textPrimary}>
                    {formatDollarAmount(largestPositionUSD)}
                  </ThemedText.DeprecatedBody>
                </StatBox>
                <StatBox>
                  <ThemedText.DeprecatedBody color={theme.textSecondary} fontSize="12px">
                    Holders &gt;$100k
                  </ThemedText.DeprecatedBody>
                  <ThemedText.DeprecatedBody color={theme.textPrimary}>{largeHoldersCount}</ThemedText.DeprecatedBody>
                </StatBox>
                <StatBox>
                  <ThemedText.DeprecatedBody color={theme.textSecondary} fontSize="12px">
                    Holders $10k-$100k
                  </ThemedText.DeprecatedBody>
                  <ThemedText.DeprecatedBody color={theme.textPrimary}>{mediumHoldersCount}</ThemedText.DeprecatedBody>
                </StatBox>
              </StatsGrid>
            </Fragment>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem', width: '100%' }}>
              <ThemedText.DeprecatedBody color={theme.textSecondary}>
                No holder data available
              </ThemedText.DeprecatedBody>
            </div>
          )}
        </ContentWrapper>
      </MainContainer>
      <ToggleContainer>
        <Switch<typeof balanceType>
          options={balanceOptions}
          activeValue={balanceType}
          setActiveValue={setBalanceType}
        />
      </ToggleContainer>
    </Fragment>
  )
}

export default HoldersDistributionView
